import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faExclamationCircle,
} from '@fortawesome/sharp-regular-svg-icons'
import { Badge, Container, Group, Loader } from '@mantine/core'
import { createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { useMemo } from 'react'

import {
  ACTION_COLUMN_ID,
  DefaultPaginatedTableImplementation,
  usePaginatedTableStack,
} from '../../../1stlog-ui/src/features/PaginatedTableStack'
import axios from '../../lib/axios'
import DownloadJobFiles from './DownloadJobFiles'
import ImportErrors from './ImportErrors'
import type { OrderJobDetailResource } from './order-jobs'
import UploadBtn from './UploadBtn'

export const ORDER_JOBS_QUERY_KEY = [`order_jobs_paginated`]

export const PaginatedOrderJobs = (): React.ReactElement => {
  const columnHelper = createColumnHelper<OrderJobDetailResource>()

  const columns = useMemo(
    () => [
      columnHelper.accessor((r) => r.id, {
        id: 'id',
        header: '#',
        enableSorting: true,
        cell: (row) => row.getValue(),
        size: 50,
        maxSize: 50,
      }),
      columnHelper.accessor('file_name', {
        id: 'file_name',
        header: i18n.t('file_name_column_header', 'Dateiname'),
        enableSorting: true,
      }),
      columnHelper.accessor('created_at', {
        id: 'created_at',
        header: i18n.t('created_at_column_helper', 'Erstellt'),
        enableSorting: true,
        cell: (r) => dayjs(r.getValue()).format('DD.MM.YYYY HH:mm'),
        maxSize: 150,
        size: 150,
      }),
      columnHelper.accessor(
        (r) => ({ completed: r.completed, downloadable: r.downloadable }),
        {
          id: 'completed',
          header: i18n.t('status_column_header', 'Status'),
          enableSorting: true,
          size: 300,
          cell: (row) => {
            if (row.getValue().completed && !row.getValue().downloadable) {
              return (
                <Badge
                  styles={{
                    inner: { display: 'flex' },
                    leftSection: { display: 'flex' },
                  }}
                  color="red"
                  leftSection={<FontAwesomeIcon icon={faExclamationCircle} />}
                >
                  {i18n.t(
                    'no_dl_status_mass_labels',
                    'Es konnten keine Labels generiert werden',
                  )}
                </Badge>
              )
            }

            return row.getValue().completed ? (
              <Badge
                styles={{
                  inner: { display: 'flex' },
                  leftSection: { display: 'flex' },
                }}
                color="green"
                leftSection={<FontAwesomeIcon icon={faCheckCircle} />}
              >
                {i18n.t(
                  'download_available_badgge_mass_labels',
                  'Download steht bereit',
                )}
              </Badge>
            ) : (
              <Badge
                styles={{
                  inner: { display: 'flex' },
                  leftSection: { display: 'flex' },
                }}
                leftSection={<Loader size={14} />}
              >
                {i18n.t('download_in_preprations', 'Labels werden vorbereitet')}
              </Badge>
            )
          },
        },
      ),

      columnHelper.accessor(
        (row) => ({
          errors: row.errors,
          file_name: row.file_name,
          downloadable: row.downloadable,
        }),
        {
          id: 'errors',
          enableSorting: false,
          header: i18n.t('errors_order_job', 'Verarbeitungsfehler'),
          cell: (row) => <ImportErrors {...row.getValue()} />,
        },
      ),
      columnHelper.accessor(
        (r) => ({
          id: r.id,
          completed: r.completed,
          file_name: r.file_name,
          downloadable: r.downloadable,
        }),
        {
          id: ACTION_COLUMN_ID,
          header: () => <UploadBtn />,
          enableSorting: false,
          cell: (row) => (
            <Group position="right">
              {row.getValue().completed && row.getValue().downloadable && (
                <DownloadJobFiles
                  completed={row.getValue().completed}
                  file_name={row.getValue().file_name}
                  jobId={row.getValue().id}
                />
              )}
            </Group>
          ),
        },
      ),
    ],
    [],
  )

  const tableStack = usePaginatedTableStack<OrderJobDetailResource>({
    queryUrl: `/api/order-jobs`,
    tableIdentifier: ORDER_JOBS_QUERY_KEY[0],
    columns,
    queryOptions: {
      refetchInterval: 20000,
    },
    axiosInstance: axios,
  })

  return (
    <Container
      py="xl"
      size="90vw"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        height: '100%',
      }}
    >
      <DefaultPaginatedTableImplementation<OrderJobDetailResource>
        {...tableStack}
      />
    </Container>
  )
}
